import React from 'react'

import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { usePanelFilterOptions } from 'features/filters/hooks/use-panel-filter-options'
import type { FilterOption } from 'features/filters/types'
import { overlayFilterCounts } from 'features/filters/utils/overlay-filter-counts'
import { stripFilterCounts } from 'features/filters/utils/strip-filter-counts'

type Params = {
  projectId: string | undefined
  panel: ChartPanel | null
  defaultFilterList: FilterOption[]
  fields?: string[]
  /** Forwarded to `usePanelFilterOptions` — see its doc for the faceted vs.
   * non-faceted contract. Defaults to `true` (picker behavior). */
  faceted?: boolean
}

/**
 * Panel-narrowed filter-options seam for any caller that already has a
 * ``ChartPanel`` in scope. Generic version of
 * ``useDashboardEffectiveFilterList`` — that wrapper builds the synthetic
 * panel from the dashboard's V1-shape filters and delegates here.
 *
 * ``fields`` names every field the caller wants counts for — typically the
 * one popover field the user has expanded. An empty / undefined value gates
 * the V2 fetch off; that path is used by the popover's "All Properties" view
 * and by callers that do not need counts (the dashboard's outer Container
 * call site).
 *
 * Returns the names-only base (``stripFilterCounts(defaultFilterList)``) with
 * the V2 response's counted fields overlaid on top. The base shows through
 * whenever ``fields`` is empty/undefined, a V2 fetch is in flight (initial
 * load, refetch after a filter / date / search change), or an error landed
 * with no prior data; the popover renders base-only count cells as Skeleton
 * placeholders. The overlay (rather than returning the response outright) is
 * what makes per-field fetching safe: a per-field response stubs out every
 * unrequested field, and the same list also feeds the popover's field list
 * and the applied filter cards.
 *
 * Stale counts — V1 or panel-stale V2 — are never shown. PROD-2077,
 * PROD-3130, PROD-3136.
 */
export const useEffectiveFilterList = ({
  projectId,
  panel,
  defaultFilterList,
  fields,
  faceted,
}: Params): FilterOption[] => {
  const { data: panelFilterOptions } = usePanelFilterOptions(projectId, panel, {
    fields,
    faceted,
  })
  const namesOnlyFallback = React.useMemo(
    () => stripFilterCounts(defaultFilterList),
    [defaultFilterList],
  )
  return React.useMemo(
    () =>
      panelFilterOptions
        ? overlayFilterCounts(namesOnlyFallback, panelFilterOptions)
        : namesOnlyFallback,
    [panelFilterOptions, namesOnlyFallback],
  )
}
